/**
 * @description Branding overlays: LynxPay (platform) vs Lynx/generic (CP/SO)
 *
 * Key features:
 * - LYNXPAY_DEFAULTS: platform hosts only (localhost, app.lynxpay.us)
 * - LYNX_GENERIC_OVERLAY: CP/SO slug chrome when fields are empty
 * - mergeTenantBranding: empties fill from the chosen overlay, never the other brand
 *
 * @dependencies none
 *
 * @notes Documented branding walk (159 documents; slug read only):
 *   site → company → SO → CP → platform_config only if tenantRoot is platform.
 *   Company / site hops and tenantRoot stay LYN-160 / websites (LYN-79 / LYN-152).
 *   This file never persists LynxPay assets into a CP row. Do not invent a third brand.
 *   Never paint LynxPay name/yellow/favicon on a CP/SO path, then swap (flash).
 *   Generic primary must contrast with auth-bg (#0a0a0b) so text-brand / bg-brand
 *   wordmarks and CTAs stay visible. Not LynxPay yellow, not chrome black.
 */

/** LynxPay platform favicon — never use as the CP/SO empty-field fill. */
export const LYNXPAY_FAVICON_URL = 'https://assets.lynx.build/platform/favicon.png';

/** Auth chrome / splash — Tailwind `auth-bg`. Overlay primary must not match this. */
export const AUTH_BG_HEX = '#0a0a0b';

/** Lynx chrome ink (zinc-200) — visible on auth-bg; not LynxPay yellow. */
export const LYNX_GENERIC_PRIMARY = '#e4e4e7';

/** Muted Lynx chrome companion (zinc-400). */
export const LYNX_GENERIC_SECONDARY = '#a1a1aa';

const LYNXPAY_DEFAULTS = {
  identity: {
    platformName: 'LynxPay',
    documentTitle: 'LynxPay',
    tagline: 'Automated Payment Solutions'
  },
  colors: {
    mode: 'dark',
    primaryColor: '#facc15',
    secondaryColor: '#eab308'
  },
  assets: {
    logoUrl: null,
    logoMarkUrl: null,
    faviconUrl: LYNXPAY_FAVICON_URL
  },
  meta: {
    themeColor: '#0a0a0b',
    description: 'Unified merchant platform'
  }
};

/**
 * Lynx / generic overlay for CP and SO slug chrome.
 * Light Lynx chrome ink on dark splash — not LynxPay yellow, name, or favicon.
 * themeColor stays auth-bg; primary/secondary must stay visible as --brand-*.
 */
export const LYNX_GENERIC_OVERLAY = {
  identity: {
    platformName: 'Lynx',
    documentTitle: 'Lynx',
    tagline: ''
  },
  colors: {
    mode: 'dark',
    primaryColor: LYNX_GENERIC_PRIMARY,
    secondaryColor: LYNX_GENERIC_SECONDARY
  },
  assets: {
    logoUrl: null,
    logoMarkUrl: null,
    faviconUrl: null
  },
  meta: {
    themeColor: AUTH_BG_HEX,
    description: ''
  }
};

/**
 * True when a branding field should override the overlay.
 * Empty string / null / undefined stay on the overlay (the LynxPay flash path).
 *
 * @param {*} value
 * @returns {boolean}
 */
export function isPresentBrandingValue(value) {
  return value !== undefined && value !== null && value !== '';
}

function mergeSection(base, incoming) {
  if (!incoming || typeof incoming !== 'object') {
    return { ...base };
  }
  const next = { ...base };
  for (const [key, value] of Object.entries(incoming)) {
    if (isPresentBrandingValue(value)) {
      next[key] = value;
    }
  }
  return next;
}

/**
 * Overlay tenant branding onto Lynx/generic or LynxPay. Missing fields stay on overlay.
 *
 * @param {object} overlay - LYNX_GENERIC_OVERLAY or LYNXPAY_DEFAULTS
 * @param {object|null|undefined} tenantBranding
 * @returns {object}
 */
export function mergeTenantBranding(overlay, tenantBranding) {
  if (!tenantBranding || Object.keys(tenantBranding).length === 0) {
    return {
      identity: { ...overlay.identity },
      colors: { ...overlay.colors },
      assets: { ...overlay.assets },
      meta: { ...overlay.meta }
    };
  }

  const identity = mergeSection(overlay.identity, tenantBranding.identity);
  // LYN-329 / 309: empty documentTitle follows Settings platformName.
  // Never infer organizationName. Overlay Lynx stays only when both are empty.
  const incomingIdentity = tenantBranding.identity || {};
  if (
    isPresentBrandingValue(incomingIdentity.platformName)
    && !isPresentBrandingValue(incomingIdentity.documentTitle)
  ) {
    identity.documentTitle = incomingIdentity.platformName;
  }

  return {
    identity,
    colors: mergeSection(overlay.colors, tenantBranding.colors),
    assets: mergeSection(overlay.assets, tenantBranding.assets),
    meta: mergeSection(overlay.meta, tenantBranding.meta)
  };
}

/**
 * Pick the overlay for a resolved host kind.
 * Slug / custom / unknown never use LynxPay. Platform does.
 *
 * @param {'platform'|'slug'|'custom'|'unknown'} kind
 * @returns {object}
 */
export function overlayForHostKind(kind) {
  return kind === 'platform' ? LYNXPAY_DEFAULTS : LYNX_GENERIC_OVERLAY;
}

export default LYNXPAY_DEFAULTS;
